import React from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { userApi, uploadApi, postApi, pointsApi, UserLevelInfo } from '../utils/api';
import toast from 'react-hot-toast';
import { User, Lock, Upload, ChevronDown, ChevronUp, Star, Trophy, MessageSquare, BarChart3, Zap, Medal, Award, TrendingUp, Folder } from 'lucide-react';

const UserCenter: React.FC = () => {
  const { user, updateUser, logout } = useAuthStore();
  const [activeTab, setActiveTab] = React.useState<'profile' | 'password'>('profile');
  const [loading, setLoading] = React.useState(false);
  const [statsExpanded, setStatsExpanded] = React.useState(false);
  const [activeDetail, setActiveDetail] = React.useState<'level' | 'growth' | 'points' | 'posts' | 'replies' | 'achievements'>('level');
  const [userPosts, setUserPosts] = React.useState<any[]>([]);
  const [userReplies, setUserReplies] = React.useState<any[]>([]);
  const [postsLoading, setPostsLoading] = React.useState(false);
  const [repliesLoading, setRepliesLoading] = React.useState(false);
  const [levelInfo, setLevelInfo] = React.useState<UserLevelInfo | null>(null);
  const [pointsHistory, setPointsHistory] = React.useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyPage, setHistoryPage] = React.useState(1);
  const [historyTotal, setHistoryTotal] = React.useState(0);

  // 个人资料表单
  const [nickname, setNickname] = React.useState(user?.nickname || '');
  const [signature, setSignature] = React.useState(user?.signature || '');
  const [avatar, setAvatar] = React.useState(user?.avatar || '');

  // 密码表单
  const [oldPassword, setOldPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');

  React.useEffect(() => {
    if (user) {
      setNickname(user.nickname);
      setSignature(user.signature || '');
      setAvatar(user.avatar || '');
      loadLevelInfo();
    }
  }, [user]);

  const loadLevelInfo = async () => {
    if (!user) return;
    try {
      const response = await pointsApi.getUserLevelInfo(user.id);
      if (response.success && response.data) {
        setLevelInfo(response.data);
      }
    } catch (error) {
      console.error('加载等级信息失败:', error);
    }
  };

  const loadPointsHistory = async (page = 1) => {
    if (!user) return;
    setHistoryLoading(true);
    try {
      const response = await pointsApi.getPointsHistory(user.id, { page, pageSize: 20 });
      if (response.success && response.data) {
        setPointsHistory(response.data.records);
        setHistoryTotal(response.data.total);
        setHistoryPage(page);
      }
    } catch (error) {
      console.error('加载积分历史失败:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadUserPosts = async () => {
    if (!user) return;
    setPostsLoading(true);
    try {
      const response = await postApi.getUserPosts(user.id, { page: 1, pageSize: user.posts_count || 100 });
      if (response?.data) {
        setUserPosts(response.data);
      }
    } catch (error) {
      console.error('加载用户帖子失败:', error);
    } finally {
      setPostsLoading(false);
    }
  };

  const loadUserReplies = async () => {
    if (!user) return;
    setRepliesLoading(true);
    try {
      const response = await postApi.getUserReplies(user.id, { page: 1, pageSize: user.replies_count || 100 });
      if (response?.data) {
        setUserReplies(response.data);
      }
    } catch (error) {
      console.error('加载用户回复失败:', error);
    } finally {
      setRepliesLoading(false);
    }
  };

  const handleUploadAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const response = await uploadApi.uploadImage(file);
      if (response.success && response.data) {
        const newAvatarUrl = response.data.url;
        setAvatar(newAvatarUrl);
        updateUser({ avatar: newAvatarUrl });
        
        // 保存到数据库
        await userApi.updateProfile({ avatar: newAvatarUrl });
        
        toast.success('头像上传成功');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '上传失败');
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      const response = await userApi.updateProfile({
        nickname,
        signature,
        avatar,
      });
      if (response.success) {
        updateUser({ nickname, signature, avatar });
        toast.success('更新成功');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('两次输入的密码不一致');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('密码长度至少6位');
      return;
    }

    try {
      setLoading(true);
      const response = await userApi.changePassword({
        oldPassword,
        newPassword,
      });
      if (response.success) {
        toast.success('密码修改成功，请重新登录');
        logout();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '修改失败');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <h2 className="text-xl font-bold mb-4">个人中心</h2>
        
        {/* 标签页 */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'profile'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <User className="w-4 h-4" />
            个人资料
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'password'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Lock className="w-4 h-4" />
            修改密码
          </button>
        </div>

        {/* 内容 */}
        <div className="p-6">
          {activeTab === 'profile' ? (
            <form onSubmit={handleUpdateProfile} className="space-y-6">
              {/* 头像 */}
              <div className="flex items-center gap-6">
                <img
                  src={avatar || `https://ui-avatars.com/api/?name=${nickname || user?.username || 'User'}&background=3b82f6&color=fff`}
                  alt={nickname || user?.username}
                  className="w-20 h-20 rounded-full"
                />
                <div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleUploadAvatar}
                      className="hidden"
                    />
                    <span className="btn btn-secondary inline-flex items-center">
                      <Upload className="w-4 h-4 mr-2" />
                      更换头像
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    支持 JPG、PNG、GIF，最大 5MB
                  </p>
                </div>
              </div>

              {/* 用户名 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  用户名
                </label>
                <input
                  type="text"
                  value={user.username}
                  disabled
                  className="input bg-gray-50"
                />
                {user.email && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center">
                    用户名不可修改 · 邮箱：<span className="text-blue-600">{user.email}</span>
                  </p>
                )}
                {!user.email && (
                  <p className="text-xs text-gray-500 mt-1">用户名不可修改 · 未绑定邮箱</p>
                )}
              </div>

              {/* 昵称 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  昵称
                </label>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="input"
                  maxLength={50}
                />
              </div>

              {/* 个性签名 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  个性签名
                </label>
                <textarea
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  className="input min-h-[100px] resize-none"
                  maxLength={500}
                  placeholder="写点什么介绍一下自己..."
                />
              </div>

              {/* 统计信息 */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div 
                  className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setStatsExpanded(!statsExpanded)}
                >
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-pink-600" />
                    <span className="font-medium text-gray-700">我的数据</span>
                  </div>
                  {statsExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </div>
                
                {/* 统计数据概览 */}
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-4 p-4">
                  <div
                    className="text-center p-3 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg cursor-pointer hover:from-yellow-100 hover:to-yellow-200 transition-all"
                    onClick={(e) => { e.stopPropagation(); setActiveDetail('level'); }}
                  >
                    <div className="flex items-center justify-center mb-1 text-2xl">
                      {levelInfo?.levelIcon || '🌱'}
                    </div>
                    <div className="text-sm text-gray-600">Lv.{user.level} {levelInfo?.levelName || ''}</div>
                  </div>
                  <div
                    className="text-center p-3 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg cursor-pointer hover:from-orange-100 hover:to-orange-200 transition-all"
                    onClick={(e) => { e.stopPropagation(); setActiveDetail('growth'); }}
                  >
                    <div className="flex items-center justify-center mb-1">
                      <Star className="w-5 h-5 text-orange-600 mr-1" />
                      <span className="text-2xl font-bold text-orange-700">{user.growth_points || 0}</span>
                    </div>
                    <div className="text-sm text-gray-600">成长值</div>
                  </div>
                  <div
                    className="text-center p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg cursor-pointer hover:from-purple-100 hover:to-purple-200 transition-all"
                    onClick={(e) => { e.stopPropagation(); setActiveDetail('points'); }}
                  >
                    <div className="flex items-center justify-center mb-1">
                      <Zap className="w-5 h-5 text-purple-600 mr-1" />
                      <span className="text-2xl font-bold text-purple-700">{user.points}</span>
                    </div>
                    <div className="text-sm text-gray-600">当前积分</div>
                  </div>
                  <div
                    className="text-center p-3 bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg cursor-pointer hover:from-pink-100 hover:to-pink-200 transition-all"
                    onClick={(e) => { e.stopPropagation(); setActiveDetail('posts'); loadUserPosts(); }}
                  >
                    <div className="flex items-center justify-center mb-1">
                      <BarChart3 className="w-5 h-5 text-pink-600 mr-1" />
                      <span className="text-2xl font-bold text-blue-700">{user.posts_count}</span>
                    </div>
                    <div className="text-sm text-gray-600">发帖数</div>
                  </div>
                  <div
                    className="text-center p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-lg cursor-pointer hover:from-green-100 hover:to-green-200 transition-all"
                    onClick={(e) => { e.stopPropagation(); setActiveDetail('replies'); loadUserReplies(); }}
                  >
                    <div className="flex items-center justify-center mb-1">
                      <MessageSquare className="w-5 h-5 text-green-600 mr-1" />
                      <span className="text-2xl font-bold text-green-700">{user.replies_count}</span>
                    </div>
                    <div className="text-sm text-gray-600">回复数</div>
                  </div>
                  <Link
                    to="/storage"
                    className="text-center p-3 bg-gradient-to-br from-cyan-50 to-blue-100 rounded-lg cursor-pointer hover:from-cyan-100 hover:to-blue-200 transition-all"
                    onClick={(e) => { e.stopPropagation(); }}
                  >
                    <div className="flex items-center justify-center mb-1">
                      <Folder className="w-5 h-5 text-cyan-600 mr-1" />
                      <span className="text-2xl font-bold text-cyan-700">📁</span>
                    </div>
                    <div className="text-sm text-gray-600">个人仓库</div>
                  </Link>
                  <Link
                    to="/messages"
                    className="text-center p-3 bg-gradient-to-br from-purple-50 to-pink-100 rounded-lg cursor-pointer hover:from-purple-100 hover:to-pink-200 transition-all"
                    onClick={(e) => { e.stopPropagation(); }}
                  >
                    <div className="flex items-center justify-center mb-1">
                      <MessageSquare className="w-5 h-5 text-purple-600 mr-1" />
                      <span className="text-2xl font-bold text-purple-700">💬</span>
                    </div>
                    <div className="text-sm text-gray-600">站内信</div>
                  </Link>
                </div>
                
                {/* 展开详情 */}
                {statsExpanded && (
                  <div className="border-t border-gray-200 p-4 bg-white">
                    {activeDetail === 'level' && (
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-800 flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-yellow-600" />
                          等级详情
                        </h4>
                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-3xl">
                              {levelInfo?.levelIcon || '🌱'}
                            </div>
                            <div className="flex-1">
                              <p className="text-lg font-bold text-gray-800">
                                {levelInfo?.levelName || `Lv.${user.level}`}
                              </p>
                              <p className="text-gray-600">
                                当前等级：<span className="font-bold text-yellow-700">Lv.{user.level}</span>
                              </p>
                              {levelInfo?.nextLevel && (
                                <p className="text-gray-600 mt-1">
                                  距离下一等级 {levelInfo.nextLevel.name} {levelInfo.nextLevel.icon}：<span className="font-bold text-orange-700">{levelInfo.nextLevel.pointsNeeded}</span> 成长值
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="mt-4">
                            <div className="h-3 bg-yellow-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 transition-all duration-500"
                                style={{ width: `${levelInfo?.progress || 0}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 text-right">等级进度：{levelInfo?.progress || 0}%</p>
                          </div>
                        </div>

                        {/* 称号展示 */}
                        {levelInfo?.titles && levelInfo.titles.length > 0 && (
                          <div>
                            <h5 className="font-medium text-gray-700 flex items-center gap-2 mb-2">
                              <Medal className="w-4 h-4 text-purple-600" />
                              我的称号
                            </h5>
                            <div className="flex flex-wrap gap-2">
                              {levelInfo.titles.map((title, index) => (
                                <span
                                  key={index}
                                  className="px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-medium"
                                >
                                  {title.icon} {title.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* 成就展示 */}
                        {levelInfo?.achievements && levelInfo.achievements.length > 0 && (
                          <div>
                            <h5 className="font-medium text-gray-700 flex items-center gap-2 mb-2">
                              <Award className="w-4 h-4 text-pink-600" />
                              已解锁成就 ({levelInfo.achievements.filter(a => a.unlockedAt).length})
                            </h5>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {levelInfo.achievements.map((achievement, index) => (
                                <div
                                  key={index}
                                  className={`p-3 rounded-lg ${achievement.unlockedAt ? 'bg-gradient-to-br from-green-50 to-emerald-50' : 'bg-gray-50 opacity-50'}`}
                                >
                                  <div className="text-2xl mb-1">{achievement.icon}</div>
                                  <p className="text-sm font-medium text-gray-700">{achievement.name}</p>
                                  <p className="text-xs text-gray-500">{achievement.description}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {activeDetail === 'growth' && (
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-800 flex items-center gap-2">
                          <Star className="w-4 h-4 text-orange-600" />
                          成长值详情
                        </h4>
                        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-4">
                          <div className="flex items-center gap-4">
                            <TrendingUp className="w-8 h-8 text-orange-600" />
                            <div>
                              <p className="text-gray-600">当前成长值：<span className="font-bold text-orange-700 text-xl">{user.growth_points || 0}</span></p>
                              <p className="text-sm text-gray-500 mt-1">成长值通过日常活跃、发帖、互动等行为获得，是衡量社区贡献的重要指标。</p>
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-medium text-gray-700 flex items-center gap-2">
                              <Zap className="w-4 h-4 text-purple-600" />
                              积分变动记录
                            </h5>
                            <button
                              onClick={() => loadPointsHistory(1)}
                              className="text-sm text-purple-600 hover:text-purple-700"
                            >
                              刷新
                            </button>
                          </div>

                          {historyLoading ? (
                            <div className="flex justify-center py-8">
                              <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                          ) : pointsHistory.length === 0 ? (
                            <div className="bg-purple-50 rounded-lg p-8 text-center">
                              <Zap className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                              <p className="text-gray-500">暂无积分记录</p>
                            </div>
                          ) : (
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                              {pointsHistory.map((record) => (
                                <div
                                  key={record.id}
                                  className="flex items-center justify-between p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                                >
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-800">{record.action_name || record.description}</p>
                                    <p className="text-xs text-gray-500">{new Date(record.created_at).toLocaleString()}</p>
                                  </div>
                                  <div className={`text-right font-bold ${record.growth_points >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {record.growth_points >= 0 ? '+' : ''}{record.growth_points} 成长值
                                    <br />
                                    <span className={`text-xs ${record.points >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                      {record.points >= 0 ? '+' : ''}{record.points} 积分
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {historyTotal > 20 && (
                                <div className="flex justify-center gap-2 mt-4">
                                  <button
                                    onClick={() => loadPointsHistory(historyPage - 1)}
                                    disabled={historyPage === 1}
                                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg disabled:opacity-50"
                                  >
                                    上一页
                                  </button>
                                  <span className="px-3 py-1 text-gray-600">
                                    第 {historyPage} 页，共 {Math.ceil(historyTotal / 20)} 页
                                  </span>
                                  <button
                                    onClick={() => loadPointsHistory(historyPage + 1)}
                                    disabled={historyPage >= Math.ceil(historyTotal / 20)}
                                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg disabled:opacity-50"
                                  >
                                    下一页
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {activeDetail === 'points' && (
                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-800 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-purple-600" />
                          {'积分详情'}
                        </h4>
                        <div className="bg-purple-50 rounded-lg p-4">
                          <p className="text-gray-600">{'当前积分：'}<span className="font-bold text-purple-700">{user.points}</span></p>
                          <div className="mt-3 space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">{'发帖获得'}</span>
                              <span className="text-green-600">+{user.posts_count * 10}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">{'回复获得'}</span>
                              <span className="text-green-600">+{user.replies_count * 2}</span>
                            </div>
                            <div className="border-t border-purple-200 pt-2 mt-2">
                              <p className="text-gray-500 text-xs">{'积分可用于兑换社区特权和礼品'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {activeDetail === 'posts' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-800 flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-pink-600" />
                            {'我的帖子'}
                          </h4>
                          <button 
                            onClick={() => loadUserPosts()}
                            className="text-sm text-pink-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            {'刷新'}
                          </button>
                        </div>
                        {postsLoading ? (
                          <div className="flex justify-center py-8">
                            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : userPosts.length === 0 ? (
                          <div className="bg-pink-50 rounded-lg p-8 text-center">
                            <BarChart3 className="w-12 h-12 text-blue-300 mx-auto mb-3" />
                            <p className="text-gray-500">{'暂无帖子'}</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-80 overflow-y-auto">
                            {userPosts.map((post) => (
                              <div 
                                key={post.id}
                                className="flex items-center gap-3 p-3 bg-pink-50 rounded-lg hover:bg-pink-100 cursor-pointer transition-colors"
                                onClick={() => window.open(`/post/${post.id}`, '_blank')}
                              >
                                <div className="flex-1 min-w-0">
                                  <h5 className="font-medium text-gray-800 truncate">{post.title}</h5>
                                  <p className="text-sm text-gray-500">{new Date(post.created_at).toLocaleDateString()}</p>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <span>{post.likes_count || 0} 赞</span>
                                  <span>{post.replies_count || 0} 回复</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {activeDetail === 'replies' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-800 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-green-600" />
                            {'我的回复'}
                          </h4>
                          <button 
                            onClick={() => loadUserReplies()}
                            className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1"
                          >
                            {'刷新'}
                          </button>
                        </div>
                        {repliesLoading ? (
                          <div className="flex justify-center py-8">
                            <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : userReplies.length === 0 ? (
                          <div className="bg-green-50 rounded-lg p-8 text-center">
                            <MessageSquare className="w-12 h-12 text-green-300 mx-auto mb-3" />
                            <p className="text-gray-500">{'暂无回复'}</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-80 overflow-y-auto">
                            {userReplies.map((reply) => (
                              <div 
                                key={reply.id}
                                className="flex items-center gap-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 cursor-pointer transition-colors"
                                onClick={() => window.open(`/post/${reply.post_id}`, '_blank')}
                              >
                                <div className="flex-1 min-w-0">
                                  <h5 className="font-medium text-gray-800 truncate">{reply.content?.substring(0, 50)}{reply.content?.length > 50 ? '...' : ''}</h5>
                                  <p className="text-sm text-gray-500">{new Date(reply.created_at).toLocaleDateString()}</p>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <span>{reply.likes_count || 0} {'赞'}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 提交按钮 */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? ('保存中...') : ('保存修改')}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-6 max-w-md">
              {/* 旧密码 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {'当前密码'}
                </label>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className="input"
                  placeholder={'请输入当前密码'}
                />
              </div>

              {/* 新密码 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {'新密码'}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input"
                  placeholder={'请输入新密码（至少6位）'}
                />
              </div>

              {/* 确认密码 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {'确认新密码'}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input"
                  placeholder={'请再次输入新密码'}
                />
              </div>

              {/* 提交按钮 */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? ('修改中...') : ('修改密码')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserCenter;
